import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { showConfirm } from './ConfirmDialog';
import {
  apiAiAssist,
  apiAiApply,
  apiAiCredits,
  apiGetMusiciansMeta,
  apiSaveMusicians,
  apiGetCrewMeta,
  apiSaveCrew,
  type AiApplyResult,
} from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import {
  buildProposalSummary,
  countMappedFields,
  AI_SECTION_LABELS,
  AI_TRANSPORT_LABELS,
  type SummarySection,
} from '../lib/aiImport';
import {
  matchPersonnel,
  applyPersonnelMatches,
  countPersonnel,
  type PersonnelDiff,
  type ProposedPersonnel,
} from '../lib/aiPersonnelImport';
import type { Musician, CrewMember } from '../types';
import { createStagePlot, listStagePlots, type StagePlotListItem } from '../api/stageplots';
import { createEmptyStagePlot } from '../shared/types/stageplot';

interface ImportRiderAIModalProps {
  bandId: string | null;
  onClose: () => void;
  /** Called after a successful apply — the parent reloads its config. */
  onApplied: (result: AiApplyResult) => void;
  /** Current band name (used for the imported plot's default name). */
  bandName?: string;
  /** Called after a stage plot is created from a PDF drawing — the parent
   * can close the modal and open the editor on the new plot. */
  onStagePlotCreated?: (created: StagePlotListItem) => void;
}

// Read a File as base64 (no data-URL prefix) so the backend can decode it
// straight into a PDF buffer.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // readAsDataURL yields "data:application/pdf;base64,<...>" — keep only <...>.
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

// Rendered inline (not via portal): `.modal-overlay` is `position: fixed`, so it
// overlays the viewport regardless of where it sits in the tree, and this keeps
// us off `react-dom` (whose type declarations are not installed in this repo).
export default function ImportRiderAIModal({ bandId, onClose, onApplied, bandName, onStagePlotCreated }: ImportRiderAIModalProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [applying, setApplying] = useState(false);
  const [sections, setSections] = useState<Record<string, unknown> | null>(null);
  const [summary, setSummary] = useState<SummarySection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [merged, setMerged] = useState<string[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // ---- OCR recovery (LOT G): a scanned PDF's text was recovered by OCR ----
    const [ocrLang, setOcrLang] = useState('auto'); // 'auto' → server default (fra+eng)
    const [ocrInfo, setOcrInfo] = useState<{
      used: boolean; langs: string | null; pages: number; warnings: string[];
    } | null>(null);
    const OCR_LANG_OPTIONS = [
      { value: 'auto', label: t('importRiderAi.ocrLangAuto') },
      { value: 'fra+eng', label: t('importRiderAi.ocrLangFra') + ' + ' + t('importRiderAi.ocrLangEng') },
      { value: 'fra', label: t('importRiderAi.ocrLangFra') },
      { value: 'eng', label: t('importRiderAi.ocrLangEng') },
      { value: 'deu', label: t('importRiderAi.ocrLangDeu') },
      { value: 'spa', label: t('importRiderAi.ocrLangSpa') },
      { value: 'ita', label: t('importRiderAi.ocrLangIta') },
    ];

  // ---- AI credit balance (metering) — gate the map button when exhausted ----
  const [credits, setCredits] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  useEffect(() => {
    apiAiCredits().then((d) => { setCredits(d.credits); setUnlimited(!!d.unlimited); }).catch(() => setCredits(null));
  }, []);
  const outOfCredits = !unlimited && credits !== null && credits <= 0;

  const canMap = !mapping && !outOfCredits && (text.trim().length > 0 || pdfBase64 !== null);

  // ---- Personnel import (a 2nd, separately-metered AI request) ----
  const [existingMusicians, setExistingMusicians] = useState<Musician[]>([]);
  const [existingCrew, setExistingCrew] = useState<CrewMember[]>([]);
  const [musiciansVersion, setMusiciansVersion] = useState<number | null>(null);
  const [crewVersion, setCrewVersion] = useState<number | null>(null);
  const [personnelDiff, setPersonnelDiff] = useState<PersonnelDiff | null>(null);
  const [personnelMapping, setPersonnelMapping] = useState(false);
  const [personnelApplying, setPersonnelApplying] = useState(false);
  const [personnelChecked, setPersonnelChecked] = useState<Set<number>>(new Set());
  const [personnelError, setPersonnelError] = useState<string | null>(null);
  const [personnelApplied, setPersonnelApplied] = useState(false);

  // ---- Drawn stage plot recovered from the PDF (LOT E) ----
  const [stagePlotCandidates, setStagePlotCandidates] = useState<
    { page: number; mime: string; dataUrl: string }[]
  >([]);
  const [stagePlotWarning, setStagePlotWarning] = useState<string | null>(null);
  const [stagePlotCreating, setStagePlotCreating] = useState(false);
  const [stagePlotCreated, setStagePlotCreated] = useState(false);
  const [stagePlotError, setStagePlotError] = useState<string | null>(null);

  // Load the band's current rider lists WITH their concurrency versions so the
  // personnel merge happens client-side and the save echoes the loaded version.
  useEffect(() => {
    if (!bandId) return;
    let cancelled = false;
    Promise.all([apiGetMusiciansMeta(bandId), apiGetCrewMeta(bandId)])
      .then(([m, c]) => {
        if (cancelled) return;
        setExistingMusicians((m.data as Musician[]) || []);
        setExistingCrew((c.data as CrewMember[]) || []);
        setMusiciansVersion(m.version);
        setCrewVersion(c.version);
      })
      .catch(() => { /* leave lists empty; the diff will just propose adds */ });
    return () => { cancelled = true; };
  }, [bandId]);

  const handleFile = useCallback(async (file: File | undefined | null) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError(t('importRiderAi.notPdf'));
      return;
    }
    setError(null);
    setMerged(null);
    setSections(null);
    setSummary([]);
    setStagePlotCandidates([]);
    setStagePlotWarning(null);
    setStagePlotCreated(false);
    setStagePlotError(null);
    try {
      const b64 = await fileToBase64(file);
      setPdfBase64(b64);
      setFileName(file.name);
      // A PDF takes precedence once chosen; clear any pasted text so the
      // request is unambiguous (text OR pdfBase64).
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('importRiderAi.readFailed'));
      reportSaveError('ai', err);
    }
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      void handleFile(e.dataTransfer.files?.[0]);
    },
    [handleFile],
  );

  const handleMap = useCallback(async () => {
    if (!bandId || mapping) return;
    if (!text.trim() && !pdfBase64) {
      setError(t('importRiderAi.pasteFirst'));
      return;
    }
    setMapping(true);
    setError(null);
    setMerged(null);
    setSections(null);
    setSummary([]);
    setStagePlotCandidates([]);
        setStagePlotWarning(null);
        setStagePlotCreated(false);
        setStagePlotError(null);
        setOcrInfo(null);
        try {
          const result = await apiAiAssist({
            task: 'import_rider',
            bandId,
            ...(pdfBase64 ? { pdfBase64 } : { text }),
            ...(pdfBase64 && ocrLang !== 'auto' ? { ocrLangs: ocrLang } : {}),
          });
          const proposed = (result.proposedFields ?? {}) as Record<string, unknown>;
          setSections(proposed);
          setSummary(buildProposalSummary(proposed));
          // The drawn stage plot lives in the image layer; the server rasterized it
          // into additive candidates. Surface them only when present.
          setStagePlotCandidates(result.stagePlotImageCandidates ?? []);
          setStagePlotWarning((result.warnings ?? []).length ? (result.warnings as string[])[0] : null);
          // OCR recovery metadata (additive — only present for scanned PDFs).
          if (result.ocrUsed) {
            setOcrInfo({
              used: true,
              langs: result.ocrLangs ?? null,
              pages: result.ocrPages ?? 0,
              warnings: result.warnings ?? [],
            });
          }
          reportSaveOk('ai');
        } catch (err) {
          setError(err instanceof Error ? err.message : t('importRiderAi.mappingFailed'));
          reportSaveError('ai', err);
        } finally {
          setMapping(false);
        }
      }, [bandId, mapping, text, pdfBase64, ocrLang]);

  const handleApply = useCallback(async () => {
    if (!bandId || applying || !sections) return;
    setApplying(true);
    setError(null);
    try {
      const result = await apiAiApply({ bandId, sections });
      setMerged(result.sectionsMerged ?? []);
      reportSaveOk('ai');
      onApplied(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('importRiderAi.applyFailed'));
      reportSaveError('ai', err);
    } finally {
      setApplying(false);
    }
  }, [bandId, applying, sections, onApplied]);

  // ---- Create a stage plot from the recovered PDF drawing ----
  const handleCreateStagePlot = useCallback(async () => {
    if (!bandId || stagePlotCreating || stagePlotCandidates.length === 0) return;
    setStagePlotCreating(true);
    setStagePlotError(null);
    try {
      const candidate = stagePlotCandidates[0];
      const name = `${bandName || 'Band'} — imported plot (${fileName || 'rider.pdf'})`;
      // Never overwrite an existing plot: if a plot with the same name already
      // exists, ask before creating a duplicate-named one.
      const existing = await listStagePlots({ bandId });
      if (existing.items.some((p) => p.name === name)) {
        const ok = await showConfirm(
          t('importRiderAi.stagePlotExistsConfirm', {
            name,
            defaultValue: `A stage plot named "${name}" already exists. Create another one anyway?`,
          }),
        );
        if (!ok) return;
      }
      const created = await createStagePlot({
        name,
        bandId,
        data: {
          ...createEmptyStagePlot(name),
          kind: 'image',
          imageUrl: candidate.dataUrl,
        },
      });
      setStagePlotCreated(true);
      reportSaveOk('stageplots');
      onStagePlotCreated?.(created);
    } catch (err) {
      setStagePlotError(err instanceof Error ? err.message : t('importRiderAi.stagePlotCreateFailed'));
      reportSaveError('stageplots', err);
    } finally {
      setStagePlotCreating(false);
    }
  }, [bandId, stagePlotCreating, stagePlotCandidates, bandName, fileName, onStagePlotCreated, t]);

  // ---- personnel handlers ----
  const handleMapPersonnel = useCallback(async () => {
    if (!bandId || personnelMapping) return;
    if (!text.trim() && !pdfBase64) {
      setPersonnelError(t('importRiderAi.pasteFirst'));
      return;
    }
    setPersonnelMapping(true);
        setPersonnelError(null);
        setPersonnelApplied(false);
        setPersonnelDiff(null);
        setOcrInfo(null);
        try {
          const result = await apiAiAssist({
            task: 'import_rider_personnel',
            bandId,
            ...(pdfBase64 ? { pdfBase64 } : { text }),
            ...(pdfBase64 && ocrLang !== 'auto' ? { ocrLangs: ocrLang } : {}),
          });
          const proposed = (result.proposedPersonnel ?? { musicians: [], crew: [] }) as ProposedPersonnel;
          const diff = matchPersonnel(proposed, existingMusicians, existingCrew);
          setPersonnelDiff(diff);
          // OCR recovery metadata (additive — only present for scanned PDFs).
          if (result.ocrUsed) {
            setOcrInfo({
              used: true,
              langs: result.ocrLangs ?? null,
              pages: result.ocrPages ?? 0,
              warnings: result.warnings ?? [],
            });
          }
      // All rows checked by default EXCEPT conflict-only matches (no fields to
      // write) — those are skipped regardless.
      const checked = new Set<number>();
      let idx = 0;
      for (const m of diff.musicians) { if (Object.keys(m.fields).length > 0) checked.add(idx); idx++; }
      for (const c of diff.crew) { if (Object.keys(c.fields).length > 0) checked.add(idx); idx++; }
      setPersonnelChecked(checked);
      reportSaveOk('ai');
    } catch (err) {
      setPersonnelError(err instanceof Error ? err.message : t('importPersonnelAi.emptyResult'));
      reportSaveError('ai', err);
    } finally {
      setPersonnelMapping(false);
    }
  }, [bandId, personnelMapping, text, pdfBase64, existingMusicians, existingCrew, ocrLang]);

  const handleApplyPersonnel = useCallback(async () => {
    if (!bandId || personnelApplying || !personnelDiff) return;
    setPersonnelApplying(true);
    setPersonnelError(null);
    setPersonnelApplied(false);
    try {
      const { musicians, crew } = applyPersonnelMatches(
        personnelDiff,
        existingMusicians,
        existingCrew,
        personnelChecked,
      );
      // Save BOTH lists through the existing versioned PUT helpers. The loaded
      // version is echoed; a 409 `stale` means another session wrote first —
      // we surface a clear message and do NOT force.
      let savedMusicians = true;
      let savedCrew = true;
      if (musicians.length !== existingMusicians.length || JSON.stringify(musicians) !== JSON.stringify(existingMusicians)) {
        await apiSaveMusicians(bandId, musicians, { version: musiciansVersion });
      }
      if (crew.length !== existingCrew.length || JSON.stringify(crew) !== JSON.stringify(existingCrew)) {
        await apiSaveCrew(bandId, crew, { version: crewVersion });
      }
      if (!savedMusicians || !savedCrew) return;
      setExistingMusicians(musicians);
      setExistingCrew(crew);
      setPersonnelApplied(true);
      setPersonnelDiff(null);
      reportSaveOk('ai');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      // A 409 `stale` must show a clear message and NOT force.
      if (msg.includes('stale') || (err as { status?: number })?.status === 409) {
        setPersonnelError(t('importPersonnelAi.stale'));
      } else {
        setPersonnelError(msg || t('importPersonnelAi.emptyResult'));
      }
      reportSaveError('ai', err);
    } finally {
      setPersonnelApplying(false);
    }
  }, [bandId, personnelApplying, personnelDiff, personnelChecked, existingMusicians, existingCrew, musiciansVersion, crewVersion]);

  const fieldCount = countMappedFields(sections);
  const personnelCounts = personnelDiff ? countPersonnel(personnelDiff) : null;

  // Flattened index -> match, for the checkbox rows.
  const personnelRows: { kind: 'musician' | 'crew'; index: number; name: string; fields: Record<string, unknown>; conflicts: string[]; action: 'enrich' | 'add' }[] = [];
  if (personnelDiff) {
    let idx = 0;
    for (const m of personnelDiff.musicians) {
      personnelRows.push({ kind: 'musician', index: idx, name: String(m.proposed.name || ''), fields: m.fields, conflicts: m.conflicts, action: m.action });
      idx++;
    }
    for (const c of personnelDiff.crew) {
      personnelRows.push({ kind: 'crew', index: idx, name: String(c.proposed.name || ''), fields: c.fields, conflicts: c.conflicts, action: c.action });
      idx++;
    }
  }

  const togglePersonnelRow = (index: number) => {
    setPersonnelChecked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('importRiderAi.title')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal ai-import-modal">
        <div className="modal-header">
          <h3 className="modal-title">{t('importRiderAi.title')}</h3>
          <button type="button" className="modal-close" aria-label={t('importRiderAi.closeDialog')} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body ai-import-body">
          {merged && merged.length > 0 ? (
            <div className="ai-import-success" role="status">
              <strong>
                {t('importRiderAi.sectionsMerged', { count: merged.length })}
              </strong>
              <span>{merged.map((s) => AI_SECTION_LABELS[s] || s).join(', ')}</span>
              <p className="ai-import-hint">
                {t('importRiderAi.applyHint')}
              </p>
            </div>
          ) : null}

          <div
            className={`ai-drop-zone${dragOver ? ' ai-drop-zone-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => {
                void handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            {fileName ? (
              <span className="ai-drop-file">
                <strong>{fileName}</strong>{t('importRiderAi.clickToReplace')}
              </span>
            ) : (
              <span>
                {t('importRiderAi.dropHint')} <strong>{t('importRiderAi.clickToBrowse')}</strong>
              </span>
            )}
          </div>

          <label className="form-group">
            <span>{t('importRiderAi.orPaste')}</span>
            <textarea
              className="form-input ai-import-textarea"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (pdfBase64) {
                  setPdfBase64(null);
                  setFileName(null);
                }
                setError(null);
                setMerged(null);
                setSections(null);
                setSummary([]);
              }}
              placeholder={t('importRiderAi.pastePh')}
              rows={6}
            />
          </label>

          {outOfCredits ? (
                      <div className="ai-import-error" role="alert">
                        {t('importRiderAi.outOfCredits')}
                      </div>
                    ) : null}

                    {/* ---- OCR language selector (only relevant for PDF imports) ---- */}
                    {pdfBase64 ? (
                      <label className="form-group ai-ocr-lang">
                        <span>{t('importRiderAi.ocrLangLabel')}</span>
                        <select
                          className="form-input"
                          value={ocrLang}
                          onChange={(e) => setOcrLang(e.target.value)}
                        >
                          {OCR_LANG_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}

                    <div className="ai-import-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleMap}
              disabled={!canMap}
            >
              {mapping ? t('importRiderAi.mapping') : outOfCredits ? t('importRiderAi.outOfCreditsBtn') : t('importRiderAi.mapWithAi')}
            </button>
          </div>

          {error ? (
                      <div className="ai-import-error" role="alert">
                        {error}
                      </div>
                    ) : null}

                    {/* ---- OCR recovery banner (LOT G): scanned PDF text was recovered ---- */}
                    {ocrInfo && ocrInfo.used ? (
                      <div className="ai-import-ocr-banner" role="status" data-ocr-banner>
                        <strong>{t('importRiderAi.ocrBanner')}</strong>
                        {ocrInfo.pages > 0 ? (
                          <span className="ai-import-hint">
                            {t('importRiderAi.ocrPagesNote', { count: ocrInfo.pages })}
                            {ocrInfo.langs ? ` (${ocrInfo.langs})` : ''}
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    {/* ---- Drawn stage plot recovered from the PDF (LOT E) ---- */}
          {stagePlotCandidates.length > 0 ? (
            <div className="ai-stageplot-block" data-stageplot-candidates>
              <h4 className="ai-section-title">{t('importRiderAi.stagePlotTitle')}</h4>
              <p className="ai-import-hint">
                {t('importRiderAi.stagePlotDetected', { page: stagePlotCandidates[0].page })}
              </p>
              <div className="ai-stageplot-previews">
                {stagePlotCandidates.map((c) => (
                  <img
                    key={c.page}
                    className="ai-stageplot-preview"
                    src={c.dataUrl}
                    alt={t('importRiderAi.stagePlotPreviewAlt', { page: c.page })}
                  />
                ))}
              </div>
              {stagePlotCreated ? (
                <div className="ai-import-success" role="status">
                  <strong>{t('importRiderAi.stagePlotCreated')}</strong>
                </div>
              ) : (
                <div className="ai-import-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCreateStagePlot}
                    disabled={stagePlotCreating}
                  >
                    {stagePlotCreating ? t('importRiderAi.stagePlotCreating') : t('importRiderAi.stagePlotCreate')}
                  </button>
                </div>
              )}
              {stagePlotError ? (
                <div className="ai-import-error" role="alert">
                  {stagePlotError}
                </div>
              ) : null}
            </div>
          ) : null}
          {stagePlotWarning ? (
            <div className="ai-import-hint" role="status">
              {stagePlotWarning}
            </div>
          ) : null}

          {summary.length > 0 ? (
            <div className="ai-import-review">
              <div className="ai-import-banner">
                {t('importRiderAi.reviewBanner')}
              </div>
              <div className="ai-import-meta">
                {t('importRiderAi.sectionsMapped', { count: summary.length })} ·{' '}
                {t('importRiderAi.fieldsMapped', { count: fieldCount })}
              </div>
              {summary.map((s) => (
                <div key={s.section} className="ai-section">
                  <h4 className="ai-section-title">{AI_SECTION_LABELS[s.section] || s.section}</h4>
                  {s.transports.map((t) => (
                    <div key={t.transport} className="ai-transport">
                      <span className="ai-transport-badge">
                        {AI_TRANSPORT_LABELS[t.transport] || t.transport}
                      </span>
                      <ul className="ai-fields">
                        {t.fields.map((f) => (
                          <li key={f.key} className="ai-field">
                            <span className="ai-field-key">{f.key}</span>
                            <span className="ai-field-value">{f.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}

          {/* ---- Personnel import (a 2nd, separately-metered AI request) ---- */}
          <div className="ai-personnel-block">
            <h4 className="ai-section-title">{t('importPersonnelAi.title')}</h4>
            <p className="ai-import-hint">{t('importPersonnelAi.subtitle')}</p>
            <p className="ai-import-hint">{t('importPersonnelAi.costHint')}</p>

            <div className="ai-import-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleMapPersonnel}
                disabled={!canMap || personnelMapping}
              >
                {personnelMapping ? t('importPersonnelAi.mappingPersonnel') : t('importPersonnelAi.mapPersonnel')}
              </button>
            </div>

            {personnelError ? (
              <div className="ai-import-error" role="alert">
                {personnelError}
              </div>
            ) : null}

            {personnelApplied ? (
              <div className="ai-import-success" role="status">
                <strong>{t('importPersonnelAi.personnelApplied')}</strong>
              </div>
            ) : null}

            {personnelDiff && personnelRows.length > 0 ? (
              <div className="ai-personnel-review">
                <div className="ai-import-meta">
                  {personnelCounts
                    ? t('importPersonnelAi.countSummary', {
                        new: personnelCounts.newMusicians + personnelCounts.newCrew,
                        enriched: personnelCounts.enrichedMusicians + personnelCounts.enrichedCrew,
                        skipped: personnelCounts.skipped,
                      })
                    : ''}
                </div>

                <div className="ai-personnel-group">
                  <h5 className="ai-personnel-group-title">{t('importPersonnelAi.musicians')}</h5>
                  {personnelRows.filter((r) => r.kind === 'musician').length === 0 ? (
                    <p className="ai-import-hint">{t('importPersonnelAi.noPersonnel')}</p>
                  ) : (
                    <ul className="ai-personnel-list">
                      {personnelRows.filter((r) => r.kind === 'musician').map((r) => (
                        <li key={r.index} className="ai-personnel-row">
                          <label className="ai-personnel-check">
                            <input
                              type="checkbox"
                              checked={personnelChecked.has(r.index)}
                              onChange={() => togglePersonnelRow(r.index)}
                            />
                            <span className="ai-personnel-name">{r.name}</span>
                          </label>
                          <span className={`ai-personnel-badge ${r.action === 'add' ? 'ai-badge-new' : 'ai-badge-enrich'}`}>
                            {r.action === 'add' ? t('importPersonnelAi.newBadge') : t('importPersonnelAi.enrichBadge')}
                          </span>
                          <span className="ai-personnel-fields">
                            {Object.keys(r.fields).length > 0
                              ? Object.entries(r.fields)
                                  .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.map((x: { name?: string }) => x?.name || String(x)).join(', ') : String(v)}`)
                                  .join(' · ')
                              : ''}
                          </span>
                          {r.conflicts.length > 0 ? (
                            <span className="ai-personnel-conflicts" title={r.conflicts.join(', ')}>
                              {t('importPersonnelAi.conflicts')}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="ai-personnel-group">
                  <h5 className="ai-personnel-group-title">{t('importPersonnelAi.crew')}</h5>
                  {personnelRows.filter((r) => r.kind === 'crew').length === 0 ? (
                    <p className="ai-import-hint">{t('importPersonnelAi.noPersonnel')}</p>
                  ) : (
                    <ul className="ai-personnel-list">
                      {personnelRows.filter((r) => r.kind === 'crew').map((r) => (
                        <li key={r.index} className="ai-personnel-row">
                          <label className="ai-personnel-check">
                            <input
                              type="checkbox"
                              checked={personnelChecked.has(r.index)}
                              onChange={() => togglePersonnelRow(r.index)}
                            />
                            <span className="ai-personnel-name">{r.name}</span>
                          </label>
                          <span className={`ai-personnel-badge ${r.action === 'add' ? 'ai-badge-new' : 'ai-badge-enrich'}`}>
                            {r.action === 'add' ? t('importPersonnelAi.newBadge') : t('importPersonnelAi.enrichBadge')}
                          </span>
                          <span className="ai-personnel-fields">
                            {Object.keys(r.fields).length > 0
                              ? Object.entries(r.fields)
                                  .map(([k, v]) => `${k}: ${String(v)}`)
                                  .join(' · ')
                              : ''}
                          </span>
                          {r.conflicts.length > 0 ? (
                            <span className="ai-personnel-conflicts" title={r.conflicts.join(', ')}>
                              {t('importPersonnelAi.conflicts')}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="ai-import-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyPersonnel}
                    disabled={personnelApplying || personnelChecked.size === 0}
                  >
                    {personnelApplying ? t('importPersonnelAi.applyingPersonnel') : t('importPersonnelAi.applyPersonnel')}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="modal-footer ai-import-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('importRiderAi.close')}
          </button>
          {summary.length > 0 && !merged ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying}
            >
              {applying ? t('importRiderAi.applying') : t('importRiderAi.applyToConfig')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
